/**
 * CrUX History API Client (Story IMC-10, Task 7)
 *
 * Fetches up to 40 weeks of weekly Core Web Vitals data from the
 * Chrome UX Report History API. This is the only way to get CrUX
 * trend data — PSI itself is point-in-time.
 *
 * API: chromeuxreport.googleapis.com/v1/records:queryHistoryRecord
 * Auth: API key (same as PageSpeed — free tier, ~150 req/min)
 * Rate: ~150 requests per minute
 *
 * ADR-013: Plain function adapter pattern
 * ADR-014: Defensive API versioning
 */

import type { AdapterResult } from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const CRUX_HISTORY_BASE = "https://chromeuxreport.googleapis.com/v1/records:queryHistoryRecord";

/** Timeout: 10s (runs in parallel with PSI calls via Promise.all in pagespeed adapter) */
const CRUX_HISTORY_TIMEOUT_MS = 10_000;

/** CrUX metric keys and their mapping to our metric keys */
const CRUX_HISTORY_METRICS: Record<string, string> = {
  largest_contentful_paint: "crux_lcp",
  first_contentful_paint: "crux_fcp",
  cumulative_layout_shift: "crux_cls",
  interaction_to_next_paint: "crux_inp",
  experimental_time_to_first_byte: "crux_ttfb",
  first_input_delay: "crux_fid",
};

// ============================================================================
// Response Types
// ============================================================================

interface CrUXHistoryDate {
  year?: number;
  month?: number;
  day?: number;
}

interface CrUXCollectionPeriod {
  firstDate?: CrUXHistoryDate;
  lastDate?: CrUXHistoryDate;
}

interface CrUXHistogramBucket {
  start?: number;
  end?: number;
  densities?: number[];
}

interface CrUXHistoryMetric {
  histogramTimeseries?: CrUXHistogramBucket[];
  percentilesTimeseries?: {
    p75s?: (number | null)[];
  };
}

interface CrUXHistoryResponse {
  record?: {
    key?: { url?: string; origin?: string };
    metrics?: Record<string, CrUXHistoryMetric>;
    collectionPeriods?: CrUXCollectionPeriod[];
  };
}

// ============================================================================
// Helpers
// ============================================================================

function cruxDateToISO(date: CrUXHistoryDate | undefined): string | null {
  if (!date?.year || !date?.month || !date?.day) return null;
  const m = String(date.month).padStart(2, "0");
  const d = String(date.day).padStart(2, "0");
  return `${date.year}-${m}-${d}`;
}

// ============================================================================
// Public API
// ============================================================================

export interface CrUXHistoryRecord {
  _reportType: "cruxHistory";
  date: string;
  periodStart: string | null;
  periodEnd: string | null;
  [metricKey: string]: unknown;
}

/**
 * Fetch CrUX History data for a URL or origin.
 * Returns up to 40 weekly records with p75 values for all 6 CrUX metrics.
 */
export async function fetchCruxHistory(
  targetUrl: string,
  apiKey: string,
  mode: "url" | "origin" = "url"
): Promise<AdapterResult<CrUXHistoryRecord[]>> {
  const body: Record<string, string> = {};
  if (mode === "url") {
    body.url = targetUrl;
  } else {
    // Extract origin from URL
    try {
      const parsed = new URL(targetUrl);
      body.origin = parsed.origin;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(`Invalid URL for CrUX History: ${targetUrl}`),
        retryable: false,
      });
    }
  }

  const apiUrl = new URL(CRUX_HISTORY_BASE);
  apiUrl.searchParams.set("key", apiKey);

  const result = await safeFetch(
    apiUrl.toString(),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    CRUX_HISTORY_TIMEOUT_MS
  );

  if (!result.success) {
    return adapterErr({
      ...result.error,
      message: sanitizeErrorMessage(result.error.message),
    });
  }

  let data: CrUXHistoryResponse;
  try {
    data = (await result.data.json()) as CrUXHistoryResponse;
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: sanitizeErrorMessage(
        `Failed to parse CrUX History response: ${err instanceof Error ? err.message : String(err)}`
      ),
      retryable: false,
    });
  }

  const record = data.record;
  if (!record?.collectionPeriods?.length || !record?.metrics) {
    // No history data available for this URL/origin
    return adapterOk([]);
  }

  const periods = record.collectionPeriods;
  const metrics = record.metrics;

  // Build one record per collection period (week)
  const records: CrUXHistoryRecord[] = [];

  for (let i = 0; i < periods.length; i++) {
    const period = periods[i]!;
    const periodEnd = cruxDateToISO(period.lastDate);

    const historyRecord: CrUXHistoryRecord = {
      _reportType: "cruxHistory",
      date: periodEnd ?? `week-${i}`,
      periodStart: cruxDateToISO(period.firstDate),
      periodEnd,
    };

    // Extract p75 for each CrUX metric at this week's index
    for (const [apiMetric, ourKey] of Object.entries(CRUX_HISTORY_METRICS)) {
      const metric = metrics[apiMetric];
      const p75 = metric?.percentilesTimeseries?.p75s?.[i];
      historyRecord[ourKey] = p75 ?? null;

      // Extract distribution densities at this week's index
      const buckets = metric?.histogramTimeseries;
      if (buckets && buckets.length >= 3) {
        historyRecord[`${ourKey}_good_pct`] = buckets[0]?.densities?.[i] ?? null;
        historyRecord[`${ourKey}_needs_improvement_pct`] = buckets[1]?.densities?.[i] ?? null;
        historyRecord[`${ourKey}_poor_pct`] = buckets[2]?.densities?.[i] ?? null;
      } else {
        historyRecord[`${ourKey}_good_pct`] = null;
        historyRecord[`${ourKey}_needs_improvement_pct`] = null;
        historyRecord[`${ourKey}_poor_pct`] = null;
      }
    }

    records.push(historyRecord);
  }

  return adapterOk(records);
}
